// MobileMoneyCheckout — guichet de paiement Mobile Money via le Payment Engine AYA.
// SANDBOX : simulation du guichet CinetPay (aucune vraie transaction).
// LIVE (futur) : redirection vers payment_url CinetPay. Aucun secret ici.
import { useEffect, useState, useCallback, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL;

const OPERATORS = [
  { id: "OM", label: "Orange Money", dot: "#FF7900" },
  { id: "WAVE", label: "Wave", dot: "#1DC4FF" },
  { id: "MTN", label: "MTN MoMo", dot: "#FFCC00" },
];

const btn = (bg, color = "#fff") => ({
  padding: "10px 12px", borderRadius: 10, background: bg, color,
  border: "none", fontWeight: 800, fontSize: 13, cursor: "pointer", width: "100%",
});

export default function MobileMoneyCheckout({ amountFcfa, purpose = "cart_checkout", metadata = {}, description, existingPayment = null, onConfirmed, onCancel }) {
  const [phase, setPhase] = useState(existingPayment ? "guichet" : "creating"); // creating | guichet | deciding | failed | cancelled | error
  const [payment, setPayment] = useState(existingPayment);
  const [operator, setOperator] = useState("OM");
  const [errMsg, setErrMsg] = useState(null);
  const createdRef = useRef(false);

  const total = amountFcfa;
  const token = localStorage.getItem("civ_user_token") || "";

  useEffect(() => {
    if (createdRef.current || existingPayment) return;
    createdRef.current = true;
    (async () => {
      try {
        const idKey = `mm_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
        const r = await fetch(`${API}/api/v3/payments/create`, {
          method: "POST",
          headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
          body: JSON.stringify({
            amount_fcfa: total,
            purpose,
            idempotency_key: idKey,
            description: description || "Paiement AYA",
            metadata,
          }),
        });
        const b = await r.json();
        if (!r.ok || !b.ok) { setPhase("error"); setErrMsg(b.error || "payment_create_failed"); return; }
        setPayment(b.payment);
        if (b.payment.mode === "LIVE" && b.payment.paymentUrl) {
          window.location.assign(b.payment.paymentUrl);
          return;
        }
        setPhase("guichet");
      } catch (_) { setPhase("error"); setErrMsg("Erreur réseau"); }
    })();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const decide = useCallback(async (outcome) => {
    if (!payment) return;
    setPhase("deciding");
    try {
      const r = await fetch(`${API}/api/v3/payments/${payment.paymentId}/sandbox/decide`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ outcome }),
      });
      const b = await r.json();
      if (!r.ok || !b.ok) { setPhase("error"); setErrMsg(b.error || "decide_failed"); return; }
      const p = b.payment;
      setPayment(p);
      if (p.status === "PAYMENT_CONFIRMED") {
        onConfirmed && onConfirmed(p.fulfillment?.result || {}, p);
      } else if (p.status === "PAYMENT_FAILED") setPhase("failed");
      else if (p.status === "PAYMENT_CANCELLED") setPhase("cancelled");
      else setPhase("guichet");
    } catch (_) { setPhase("error"); setErrMsg("Erreur réseau"); }
  }, [payment, token, onConfirmed]);

  return (
    <div data-testid="mm-checkout" style={{
      padding: 12, borderRadius: 12,
      background: "linear-gradient(135deg, rgba(16,185,129,0.08), rgba(255,126,27,0.06))",
      border: "1px solid rgba(16,185,129,0.35)",
      display: "flex", flexDirection: "column", gap: 10,
    }}>
      <div style={{ fontSize: 11, fontWeight: 800, letterSpacing: 0.6, textTransform: "uppercase", color: "#059669", display: "flex", alignItems: "center", gap: 6 }}>
        <span aria-hidden>📱</span><span>Paiement Mobile Money</span>
        <span data-testid="mm-sandbox-badge" style={{ marginLeft: "auto", fontSize: 9.5, padding: "2px 7px", borderRadius: 999, background: "rgba(245,158,11,0.18)", color: "#B45309", border: "1px solid rgba(245,158,11,0.4)" }}>SANDBOX — test</span>
      </div>

      {phase === "creating" && <div style={{ fontSize: 12.5, opacity: 0.75 }}>⏳ Initialisation du paiement…</div>}
      {phase === "deciding" && <div data-testid="mm-processing" style={{ fontSize: 12.5, opacity: 0.75 }}>⏳ Vérification du paiement auprès du provider…</div>}

      {phase === "guichet" && payment && (
        <>
          <div style={{ fontSize: 13, fontWeight: 700 }}>
            Montant : <span data-testid="mm-amount">{total.toLocaleString("fr-FR")} FCFA</span>
            <code style={{ display: "block", fontSize: 10, opacity: 0.5, marginTop: 2 }}>{payment.paymentId}</code>
          </div>
          <div style={{ display: "flex", gap: 6 }}>
            {OPERATORS.map((op) => (
              <button key={op.id} data-testid={`mm-operator-${op.id}`} onClick={() => setOperator(op.id)} style={{
                flex: 1, padding: "8px 6px", borderRadius: 10, fontSize: 11.5, fontWeight: 700, cursor: "pointer",
                border: `1.5px solid ${operator === op.id ? op.dot : "rgba(0,0,0,0.12)"}`,
                background: operator === op.id ? `${op.dot}22` : "transparent",
                color: "var(--civ-text, #0F172A)",
              }}>
                <span style={{ display: "inline-block", width: 8, height: 8, borderRadius: 99, background: op.dot, marginRight: 5 }} />
                {op.label}
              </button>
            ))}
          </div>
          <button data-testid="mm-pay-confirm" onClick={() => decide("ACCEPTED")} style={btn("linear-gradient(135deg, #10B981, #059669)")}>
            ✅ Payer {total.toLocaleString("fr-FR")} FCFA (simulation)
          </button>
          <div style={{ display: "flex", gap: 6 }}>
            <button data-testid="mm-pay-refuse" onClick={() => decide("REFUSED")} style={{ ...btn("transparent", "#B91C1C"), border: "1px solid #EF4444", fontWeight: 700, fontSize: 12 }}>
              ❌ Simuler un refus
            </button>
            <button data-testid="mm-pay-cancel" onClick={() => decide("CANCELLED")} style={{ ...btn("transparent", "#64748B"), border: "1px solid rgba(0,0,0,0.15)", fontWeight: 700, fontSize: 12 }}>
              Annuler le paiement
            </button>
          </div>
        </>
      )}

      {phase === "failed" && (
        <div data-testid="mm-failed" style={{ fontSize: 12.5, color: "#B91C1C", fontWeight: 700 }}>
          ❌ Paiement refusé par l&apos;opérateur (simulation). Aucun débit effectué.
        </div>
      )}
      {phase === "cancelled" && (
        <div data-testid="mm-cancelled" style={{ fontSize: 12.5, color: "#92400E", fontWeight: 700 }}>
          🚫 Paiement annulé. Aucun débit effectué.
        </div>
      )}
      {phase === "error" && (
        <div data-testid="mm-error" style={{ fontSize: 12.5, color: "#B91C1C" }}>
          ⚠️ {errMsg || "Erreur inconnue"}
        </div>
      )}

      {["failed", "cancelled", "error", "guichet"].includes(phase) && (
        <button data-testid="mm-back" onClick={onCancel} style={{ padding: "7px 10px", borderRadius: 8, background: "transparent", color: "var(--civ-text, #334155)", border: "1px solid rgba(0,0,0,0.12)", fontSize: 11.5, fontWeight: 600, cursor: "pointer" }}>
          ← Revenir au choix du paiement
        </button>
      )}
    </div>
  );
}
